import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import { useTheme } from '@/services/theme';
import { breakpoints } from '@/shared/styles';
import { useBreakpointDown } from '@/shared/utils';

import ModularityAnimation from './modularity.json';
import HiddenComplexityAnimation from './hidden_complexity.json';
import PermissionlessAnimation from './permissionless.json';
import ZeroDevelopmentAnimation from './zero_development.json';

const FeaturesContainer = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  position: relative;
  align-items: center;
  height: 100%;

  padding: 0 20px;
  margin-top: 60px;

  width: 100%;
  max-width: 1200px;

  @media (min-width: ${breakpoints.tabletSM}px) {
    margin-top: 64px;
    grid-template-columns: 1fr 1fr;
    padding: 0 16px;
  }

  @media (min-width: ${breakpoints.laptopSM}px) {
    padding: 0;
  }

  .Feature__container {
    border: 1px solid #1f2028;
    background: #18191f;
    border-radius: 38px;
    padding: 24px;
    display: flex;
    flex-direction: row;
    gap: 20px;

    height: 100%;
    width: 100%;
    max-width: calc(100vw - 40px);

    @media (min-width: ${breakpoints.tabletSM}px) {
      padding: 32px;
      border-radius: 46px;
    }

    @media (min-width: ${breakpoints.tabletXL}px) {
      padding: 58px;
      border-radius: 56px;
    }

    &:hover {
      outline: 2px solid rgba(37, 38, 47, 1);
    }
  }

  .Feature__icon_container {
    min-width: 80px;
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (min-width: ${breakpoints.tabletSM}px) {
      min-width: 80px;
      width: 80px;
      height: 80px;
    }

    @media (min-width: ${breakpoints.tabletLG}px) {
      min-width: 120px;
      width: 120px;
      height: 120px;
    }
  }

  .Feature__description_container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0px;
    text-align: left;

    @media (min-width: ${breakpoints.tabletSM}px) {
      gap: 10px;
    }
  }
`;

type animationId = 'permissionless' | 'modularity' | 'hiddenComplexity' | 'zeroDevelopment';

export const Features = () => {
  const { theme } = useTheme();
  const lottiePermissionlessRef = useRef<LottieRefCurrentProps | null>(null);
  const lottieModularityRef = useRef<LottieRefCurrentProps | null>(null);
  const lottieHiddenComplexityRef = useRef<LottieRefCurrentProps | null>(null);
  const lottieZeroDevelopmentRef = useRef<LottieRefCurrentProps | null>(null);
  // Start animations on hover. Keeps playing if the user keeps hovering over the section
  const [isHovered, setIsHovered] = useState<Record<animationId, boolean>>({
    permissionless: false,
    modularity: false,
    hiddenComplexity: false,
    zeroDevelopment: false,
  });

  const isTablet = useBreakpointDown(breakpoints.tabletLG);

  // stopped by default, and playing while hovering over section (autoplay on scroll on mobile)
  useEffect(() => {
    lottiePermissionlessRef.current?.stop();
    lottieModularityRef.current?.stop();
    lottieHiddenComplexityRef.current?.stop();
    lottieZeroDevelopmentRef.current?.stop();
  }, [isTablet]);

  const onMouseEnter = (id: animationId, lottieRef: LottieRefCurrentProps | null) => {
    if (isTablet) {
      return;
    }

    setIsHovered(prev => ({ ...prev, [id]: true }));

    lottieRef?.play();
  };

  const onMouseLeave = (id: animationId) => {
    if (isTablet) {
      return;
    }

    setIsHovered(prev => ({ ...prev, [id]: false }));
  };

  const onLoopComplete = (id: animationId, lottieRef: LottieRefCurrentProps | null) => {
    if (!isHovered[id]) {
      lottieRef?.stop();
    }
  };

  return (
    <FeaturesContainer theme={theme}>
      <div
        className="Feature__container"
        onMouseEnter={() => onMouseEnter('permissionless', lottiePermissionlessRef.current)}
        onMouseLeave={() => onMouseLeave('permissionless')}
      >
        <div className="Feature__icon_container">
          <Lottie
            lottieRef={lottiePermissionlessRef}
            animationData={PermissionlessAnimation}
            loop
            onLoopComplete={() => onLoopComplete('permissionless', lottiePermissionlessRef.current)}
            interactivity={
              isTablet
                ? {
                    mode: 'scroll',
                    actions: [
                      {
                        visibility: [0, 0.5],
                        type: 'stop',
                        frames: [1],
                      },
                      {
                        visibility: [0.5, 1.0],
                        type: 'play',
                        frames: [1, 240],
                      },
                    ],
                  }
                : undefined
            }
          />
        </div>
        <div className="Feature__description_container">
          <h4>Permissionless</h4>
          <div className="Content__description3">
            No intermediaries — design and deploy your own investment product.
          </div>
        </div>
      </div>

      <div
        className="Feature__container"
        onMouseEnter={() => onMouseEnter('modularity', lottieModularityRef.current)}
        onMouseLeave={() => onMouseLeave('modularity')}
      >
        <div className="Feature__icon_container">
          <Lottie
            lottieRef={lottieModularityRef}
            animationData={ModularityAnimation}
            loop
            onLoopComplete={() => onLoopComplete('modularity', lottieModularityRef.current)}
            interactivity={
              isTablet
                ? {
                    mode: 'scroll',
                    actions: [
                      {
                        visibility: [0, 0.5],
                        type: 'stop',
                        frames: [1],
                      },
                      {
                        visibility: [0.5, 1.0],
                        type: 'play',
                        frames: [1, 240],
                      },
                    ],
                  }
                : undefined
            }
          />
        </div>
        <div className="Feature__description_container">
          <h4>Modular</h4>
          <div className="Content__description3">
            Flexible architecture that supports a vast range of assets, protocols, and integrations.
            Responsive framework that adapts to your investment strategy.
          </div>
        </div>
      </div>

      <div
        className="Feature__container"
        onMouseEnter={() => onMouseEnter('hiddenComplexity', lottieHiddenComplexityRef.current)}
        onMouseLeave={() => onMouseLeave('hiddenComplexity')}
      >
        <div className="Feature__icon_container">
          <Lottie
            lottieRef={lottieHiddenComplexityRef}
            animationData={HiddenComplexityAnimation}
            loop
            onLoopComplete={() =>
              onLoopComplete('hiddenComplexity', lottieHiddenComplexityRef.current)
            }
            interactivity={
              isTablet
                ? {
                    mode: 'scroll',
                    actions: [
                      {
                        visibility: [0, 0.5],
                        type: 'stop',
                        frames: [1],
                      },
                      {
                        visibility: [0.5, 1.0],
                        type: 'play',
                        frames: [1, 480],
                      },
                    ],
                  }
                : undefined
            }
          />
        </div>
        <div className="Feature__description_container">
          <h4>Secure</h4>
          <div className="Content__description3">
            Thorough security standards, live monitoring and independent audits for every product
            feature release. Confidence that your capital is safeguarded at every layer.
          </div>
        </div>
      </div>

      <div
        className="Feature__container"
        onMouseEnter={() => onMouseEnter('zeroDevelopment', lottieZeroDevelopmentRef.current)}
        onMouseLeave={() => onMouseLeave('zeroDevelopment')}
      >
        <div className="Feature__icon_container">
          <Lottie
            lottieRef={lottieZeroDevelopmentRef}
            animationData={ZeroDevelopmentAnimation}
            loop
            onLoopComplete={() =>
              onLoopComplete('zeroDevelopment', lottieZeroDevelopmentRef.current)
            }
            interactivity={
              isTablet
                ? {
                    mode: 'scroll',
                    actions: [
                      {
                        visibility: [0, 0.5],
                        type: 'stop',
                        frames: [1],
                      },
                      {
                        visibility: [0.5, 1.0],
                        type: 'play',
                        frames: [1, 240],
                      },
                    ],
                  }
                : undefined
            }
          />
        </div>
        <div className="Feature__description_container">
          <h4>Streamlined</h4>
          <div className="Content__description3">
            The vault architecture abstracts away complexities, enabling sophisticated yield
            strategies that are easy to create and use.
          </div>
        </div>
      </div>
    </FeaturesContainer>
  );
};
